import { useEffect, useState } from 'react'
import { Select, SelectContent, SelectItem, SelectSeparator, SelectTrigger, SelectValue } from '@/components/ui/select'
import { showErrorToast, showSuccessToast } from '@/lib/toast'
import { ApiError } from '@/lib/api/client'
import {
  changeImportSupplyMapping,
  getImportSupplyMappingSuppliers,
  getImportSupplyMappings,
  type ImportSupplyMappingDto,
} from '@/lib/api/v2/settings'

const MAKE = '__make__'
const NONE = '__none__'
const TO_SAY = '__to_say__'

function valueOf(mapping: ImportSupplyMappingDto): string {
  if (mapping.mode === 'make') return MAKE
  if (mapping.mode === 'none') return NONE
  return mapping.supplierId ?? TO_SAY
}

/**
 * SOLY-IMPORT-003 S12 — the answers the Winner import remembered: « HÄCKER Concept130 → Acheter chez
 * Häcker ». Changing one applies to the next imports; lines already imported keep their supply.
 * Hidden until an import has made one.
 */
export function WinnerCatalogSupplyMappings() {
  const [mappings, setMappings] = useState<ImportSupplyMappingDto[]>([])
  const [suppliers, setSuppliers] = useState<{ id: string; name: string }[]>([])
  const [savingId, setSavingId] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    Promise.all([getImportSupplyMappings(), getImportSupplyMappingSuppliers()])
      .then(([rows, options]) => {
        if (cancelled) return
        setMappings(rows)
        setSuppliers(options)
      })
      .catch(() => {
        if (!cancelled) setMappings([])
      })
    return () => {
      cancelled = true
    }
  }, [])

  if (mappings.length === 0) return null

  const change = async (mapping: ImportSupplyMappingDto, value: string) => {
    if (value === valueOf(mapping)) return
    const body = value === MAKE
      ? { mode: 'make' as const }
      : value === NONE
        ? { mode: 'none' as const }
        : { mode: 'buy' as const, supplierId: value === TO_SAY ? null : value }
    setSavingId(mapping.id)
    try {
      const updated = await changeImportSupplyMapping(mapping.id, body)
      setMappings((prev) => prev.map((m) => (m.id === updated.id ? updated : m)))
      showSuccessToast('Appro mis à jour pour les prochains imports.')
    } catch (e) {
      showErrorToast(e instanceof ApiError && e.message ? e.message : "Erreur lors de l'enregistrement.")
    } finally {
      setSavingId(null)
    }
  }

  return (
    <div className="space-y-3 pt-4" data-testid="settings-winner-supply-mappings">
      <div>
        <h3 className="text-sm font-medium">Catalogues Winner : chez qui commander</h3>
        <p className="text-xs text-muted-foreground">
          L'appro que l'import Winner donne aux lignes de chaque catalogue. Un changement vaut pour les prochains imports : les devis déjà importés ne bougent pas.
        </p>
      </div>
      <div className="rounded-lg border divide-y">
        {mappings.map((mapping) => (
          <div
            key={mapping.id}
            className="grid gap-2 px-4 py-3 sm:grid-cols-[minmax(0,1fr)_16rem] sm:items-center"
            data-testid={`settings-winner-supply-row-${mapping.catalogKey}`}
          >
            <div className="min-w-0">
              <div className="text-sm font-medium truncate">{mapping.catalogName ?? mapping.catalogKey}</div>
              <div className="text-xs text-muted-foreground">Catalogue Winner {mapping.catalogKey}</div>
            </div>
            <Select value={valueOf(mapping)} onValueChange={(value) => change(mapping, value)} disabled={savingId === mapping.id}>
              <SelectTrigger
                aria-label={`Appro du catalogue ${mapping.catalogName ?? mapping.catalogKey}`}
                data-testid={`settings-winner-supply-select-${mapping.catalogKey}`}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={TO_SAY}>Acheter · fournisseur à préciser</SelectItem>
                {suppliers.map((s) => (
                  <SelectItem key={s.id} value={s.id}>Acheter chez {s.name}</SelectItem>
                ))}
                <SelectSeparator />
                <SelectItem value={MAKE}>Fabriquer</SelectItem>
                <SelectItem value={NONE}>Aucun</SelectItem>
              </SelectContent>
            </Select>
          </div>
        ))}
      </div>
    </div>
  )
}
